<template>
  <section>
    <base-dialog
      :value="value"
      :is-open="value"
      :class-list="visibilityClass"
      @input="v => $emit('input', v)"
      @close="closeDialog"
      @click:outside="closeDialog"
    >
      <template #dialog-title>
        Bargeldsammlung {{ collection.name }}
      </template>
      <template #dialog-text>
        <div class="pa-4">
          Es sind bereits Bargeldsammlungen eingetragen:
        </div>
        <v-data-table
          :headers="tableHeaders"
          :items="cashPayments"
          dense
        >
          <template #item.state="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  :color="item.state === 'processed' ? 'success' : 'warning'"
                >
                  {{ item.state === 'processed' ? mdiCheckCircle : mdiAlert }}
                </v-icon>
              </template>
              <span>{{ item.state === 'processed' ? 'Bestätigt' : 'unbestätigt' }}</span>
            </v-tooltip>
          </template>
          <template #item.amount="{ item }">
            {{ item.amount | amount }} {{ item.currency | currencySymbol }}
          </template>
          <template #item.created_at="{ item }">
            {{ item.created_at | isoToHuman('DD.MM.YYYY') }}
          </template>
          <template #item.created_by="{ item }">
          <span v-if="item.created_by">
            {{ allUsers[item.created_by]?.name || 'Unbekannt' }}
          </span>
            <span v-else>
            Anonym
          </span>
          </template>
          <template #item.changed_by="{ item }">
          <span v-if="item.changed_by">
            {{ allUsers[item.changed_by]?.name || 'Unbekannt' }}
          </span>
          </template>
          <template #item.confirmed_by="{ item }">
          <span v-if="item.confirmed_by">
            {{ allUsers[item.confirmed_by]?.name || 'Unbekannt' }}
          </span>
          </template>
          <template #item._actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-if="item.state === 'entered'"
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  icon
                  @click="editCashPayment = item"
                  :disabled="!canEdit(item)"
                >
                  <v-icon>{{ mdiPencil }}</v-icon>
                </v-btn>
              </template>
              <span>Bearbeiten</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-if="item.state === 'entered'"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!canConfirm(item) || confirmLoading"
                  color="primary"
                  icon
                  @click="selectedCashPayment = item"
                >
                  <v-icon>{{ mdiCheck }}</v-icon>
                </v-btn>
              </template>
              <span>Bestätigen</span>
            </v-tooltip>
          </template>
          <template #footer.prepend>
            <v-row>
              <v-col cols="12" >
                <strong>Summe: </strong> {{ totalCashAmount | amount }} {{ currentOrganization.currency | currencySymbol }}
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </template>
      <template #dialog-actions>
        <v-btn
          text
          color="neutral"
          @click="closeDialog"
        >
          Schließen
        </v-btn>
        <v-btn
          color="primary"
          @click="cashCollectionDialog = true"
          :disabled="!canAdd()"
        >
          Neue Barsammlung
        </v-btn>
      </template>
    </base-dialog>
    <base-dialog
      :is-open="!!selectedCashPayment"
      :persistent="confirmLoading"
      :close-on-click-outside="!confirmLoading"
      :hide-closing-options="confirmLoading"
      @close="selectedCashPayment = null"
    >
      <template #dialog-title>
        Eintragung bestätigen
      </template>

      <template #dialog-text>
        <div>
          Sind Sie sicher? Dann kann an diesem Eintrag nichts mehr verändert werden.
        </div>
      </template>

      <template #dialog-actions>
        <v-btn
          text
          color="neutral"
          :disabled="confirmLoading"
          @click="selectedCashPayment = null"
        >
          Abbrechen
        </v-btn>
        <v-btn
          text
          color="primary"
          :loading="confirmLoading"
          :disabled="confirmLoading"
          @click="confirmCashPayment"
        >
          Bestätigen
        </v-btn>
      </template>

    </base-dialog>
    <add-cash-collection-dialog
      v-model="cashCollectionDialog"
      :organization="currentOrganization"
      :collection="collection"
      :edit-cash-payment="editCashPayment"
      @close="closeEditCashCollectionDialog"
    />
  </section>
</template>
<script>
import BaseDialog from "@/components/UI/BaseDialog.vue"
import { mapGetters, mapState } from "vuex"
import {
  mdiCheckCircle,
  mdiAlert,
  mdiCheck,
  mdiPencil,
} from "@mdi/js"
import { CONFIRM_CASH_PAYMENT } from "@/store/action-types"
import AddCashCollectionDialog from "@/components/collection/AddCashCollectionDialog.vue"
import currency from "../filters/currency"
import {
  CONFIRM_CASH_PAYMENT_4EYE,
  CONFIRM_CASH_PAYMENT_SELF,
  WRITE_CASH_PAYMENT_ALL,
  WRITE_CASH_PAYMENT_OWN
} from '@/lib/permission-tools'

export default {
  name: 'CashPaymentDialog',
  components: { AddCashCollectionDialog, BaseDialog },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    collection: {
      type: Object,
      required: true
    },
  },
  data () {
    return {
      isConfirmationDialogOpen: true,
      selectedCashPayment: null,
      editCashPayment: null,
      cashCollectionDialog: false,
      confirmLoading: false,
      mdiCheck,
      mdiCheckCircle,
      mdiAlert,
      mdiPencil,
    }
  },
  computed: {
    ...mapGetters('payment', ['cashPaymentsByCollectionPlan']),
    ...mapGetters('user', ['allUsers', 'isSuperuser', 'effectivePermissionsByUrl']),
    ...mapState('user', ['user']),
    ...mapGetters('organization', ['currentOrganization']),
    cashPayments () {
      return (this.cashPaymentsByCollectionPlan[this.collection.url] || [])
        .filter(payment => payment.organization === this.currentOrganization.url)
        .sort((paymentA, paymentB) => new Date(paymentB.created_at) - new Date(paymentA.created_at))
    },
    tableHeaders () {
      return [
        { text: 'Status', value: 'state' },
        { text: 'Betrag', value: 'amount', align: 'end' },
        { text: 'Datum der Sammlung', value: 'created_at' },
        { text: 'Eintragung durch', value: 'created_by' },
        { text: 'Geändert durch', value: 'changed_by' },
        { text: 'Bestätigt durch', value: 'confirmed_by' },
        { text: '', value: '_actions', sortable: false }
      ]
    },
    totalCashAmount () {
      return this.cashPayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
    },
    visibilityClass () {
      return this.cashCollectionDialog ? 'd-none' : ''
    },
    effectivePermissions () {
      return this.effectivePermissionsByUrl[this.currentOrganization?.url] ?? new Set()
    },
  },
  mounted () {
    this.cashCollectionDialog = !this.collection.entered_state_payments_exists && this.cashPayments.length === 0
  },
  methods: {
    currency,
    closeDialog () {
      this.selectedCashPayment = null
      this.editCashPayment = null
      this.isConfirmationDialogOpen = false
      this.cashCollectionDialog = false
      this.$emit('input', false)
      this.$emit('close')
    },
    closeEditCashCollectionDialog () {
      this.cashCollectionDialog = false
      this.editCashPayment = null
      this.selectedCashPayment = null
    },
    async confirmCashPayment () {
      if (this.confirmLoading) return
      const target = this.selectedCashPayment
      this.confirmLoading = true
      try {
        const success = await this.$store.dispatch('payment/' + CONFIRM_CASH_PAYMENT, { cashPayment: target })
        if (success && this.selectedCashPayment === target) {
          this.selectedCashPayment = null
        }
      } finally {
        this.confirmLoading = false
      }
    },
    canAdd () {
      return this.isSuperuser || (
        this.effectivePermissions.has(WRITE_CASH_PAYMENT_OWN)
      )
    },
    canEdit (cashPayment) {
      return this.isSuperuser || (
        this.effectivePermissions.has(WRITE_CASH_PAYMENT_ALL)
      ) || (
        this.effectivePermissions.has(WRITE_CASH_PAYMENT_OWN) && this.user?.url === cashPayment.created_by
      )
    },
    canConfirm (cashPayment) {
      return this.isSuperuser || (
        this.effectivePermissions.has(CONFIRM_CASH_PAYMENT_SELF)
      ) || (
        this.effectivePermissions.has(CONFIRM_CASH_PAYMENT_4EYE) && this.user?.url !== cashPayment.created_by
      )
    },
  },
  watch: {
    editCashPayment (newValue) {
      if (newValue) {
        this.cashCollectionDialog = true
      }
    }
  }
}
</script>
